import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";

const TIME_OPTIONS = [
  { label: "7:00 AM", value: "07:00:00" },
  { label: "7:30 AM", value: "07:30:00" },
  { label: "8:00 AM", value: "08:00:00" },
  { label: "8:30 AM", value: "08:30:00" },
  { label: "9:00 AM", value: "09:00:00" },
  { label: "9:30 AM", value: "09:30:00" },
  { label: "10:00 AM", value: "10:00:00" },
  { label: "10:30 AM", value: "10:30:00" },
  { label: "11:00 AM", value: "11:00:00" },
  { label: "11:30 AM", value: "11:30:00" },
  { label: "12:00 PM", value: "12:00:00" },
  { label: "12:30 PM", value: "12:30:00" },
  { label: "1:00 PM", value: "13:00:00" },
  { label: "1:30 PM", value: "13:30:00" },
  { label: "2:00 PM", value: "14:00:00" },
  { label: "2:30 PM", value: "14:30:00" },
  { label: "3:00 PM", value: "15:00:00" },
  { label: "3:30 PM", value: "15:30:00" },
  { label: "4:00 PM", value: "16:00:00" },
];

function timeLabel(value) {
  return TIME_OPTIONS.find((t) => t.value === value)?.label || "";
}

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getWeekDates(weekOffset) {
  const today = new Date();
  const dow = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1) + weekOffset * 7);
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function getMonthGrid(monthOffset) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + monthOffset;
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const grid = [];
  for (let i = 0; i < startDow; i++) grid.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) {
    grid.push(new Date(year, month, d));
  }
  while (grid.length % 7 !== 0) grid.push(null);
  return { grid, year, month };
}

function isPast(date) {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

function isToday(date) {
  if (!date) return false;
  return formatDate(date) === formatDate(new Date());
}

export function FieldTechRoster() {
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState("week");
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [rosterMap, setRosterMap] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayState, setDayState] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchRoster();
  }, []);

  const fetchRoster = async () => {
    try {
      const res = await axios.get(`${API}/tech-roster/`);
      const map = {};
      res.data.forEach((e) => {
        map[e.date] = e;
      });
      setRosterMap(map);
    } catch (err) {
      console.error(err);
    }
  };

  const getState = (dateStr) => {
    if (dayState[dateStr]) return dayState[dateStr];
    const ex = rosterMap[dateStr];
    return {
      is_available: ex ? ex.is_available : false,
      start_time: ex ? ex.start_time : "",
      end_time: ex ? ex.end_time : "",
    };
  };

  const updateField = (dateStr, field, value) => {
    setDayState((prev) => ({
      ...prev,
      [dateStr]: { ...getState(dateStr), [field]: value },
    }));
  };

  const handleSave = async () => {
    if (!selectedDay) return;
    const state = getState(selectedDay);
    setSaving(true);
    try {
      const existing = rosterMap[selectedDay];
      const payload = {
        date: selectedDay,
        is_available: state.is_available,
        start_time:
          state.is_available && state.start_time ? state.start_time : null,
        end_time: state.is_available && state.end_time ? state.end_time : null,
      };
      if (existing) {
        await axios.patch(`${API}/tech-roster/${existing.id}/`, payload);
      } else {
        await axios.post(`${API}/tech-roster/`, payload);
      }
      await fetchRoster();
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        setSelectedDay(null);
      }, 1500);
    } catch (err) {
      console.error(err.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const weekDates = getWeekDates(weekOffset);
  const { grid, year, month } = getMonthGrid(monthOffset);
  const monthName = new Date(year, month, 1).toLocaleDateString("en-AU", {
    month: "long",
    year: "numeric",
  });
  const weekLabel =
    weekOffset === 0
      ? "This Week"
      : weekOffset === 1
        ? "Next Week"
        : `+${weekOffset} weeks`;

  const selectStyle = {
    flex: 1,
    padding: "8px 10px",
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    fontSize: "13px",
    outline: "none",
    backgroundColor: colors.white,
  };

  // Inline edit panel — used for both desktop inline and mobile bottom sheet
  const renderEditPanel = (dateStr, inline = false) => {
    const state = getState(dateStr);
    const dateObj = new Date(dateStr + "T00:00:00");
    const past = isPast(dateObj);

    const panelContent = (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "14px",
          }}
        >
          <div
            style={{
              fontWeight: "700",
              fontSize: "15px",
              color: colors.darkGrey,
            }}
          >
            {dateObj.toLocaleDateString("en-AU", {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </div>
          <button
            onClick={() => setSelectedDay(null)}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: colors.slate,
            }}
          >
            ✕
          </button>
        </div>

        {past ? (
          <div
            style={{
              textAlign: "center",
              padding: "8px 0",
              color: colors.slate,
            }}
          >
            {rosterMap[dateStr] ? (
              <span
                style={{
                  color: rosterMap[dateStr].is_available
                    ? colors.teal
                    : colors.slate,
                  fontWeight: "600",
                }}
              >
                {rosterMap[dateStr].is_available
                  ? `✅ Available: ${timeLabel(rosterMap[dateStr].start_time)} – ${timeLabel(rosterMap[dateStr].end_time)}`
                  : "❌ Unavailable"}
              </span>
            ) : (
              <span style={{ color: "#aaa" }}>No availability set</span>
            )}
          </div>
        ) : (
          <>
            {/* Toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "14px",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: colors.darkGrey,
                }}
              >
                Available
              </span>
              <div
                onClick={() =>
                  updateField(dateStr, "is_available", !state.is_available)
                }
                style={{
                  width: "48px",
                  height: "26px",
                  borderRadius: "13px",
                  backgroundColor: state.is_available ? colors.teal : "#ccc",
                  position: "relative",
                  cursor: "pointer",
                  transition: "background 0.2s",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "3px",
                    left: state.is_available ? "24px" : "3px",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }}
                />
              </div>
            </div>

            {state.is_available && (
              <div
                style={{ display: "flex", gap: "10px", marginBottom: "14px" }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: colors.slate,
                      textTransform: "uppercase",
                      marginBottom: "4px",
                    }}
                  >
                    Start
                  </div>
                  <select
                    style={selectStyle}
                    value={state.start_time || ""}
                    onChange={(e) =>
                      updateField(dateStr, "start_time", e.target.value)
                    }
                  >
                    <option value="">Select...</option>
                    {TIME_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: colors.slate,
                      textTransform: "uppercase",
                      marginBottom: "4px",
                    }}
                  >
                    Finish
                  </div>
                  <select
                    style={selectStyle}
                    value={state.end_time || ""}
                    onChange={(e) =>
                      updateField(dateStr, "end_time", e.target.value)
                    }
                  >
                    <option value="">Select...</option>
                    {TIME_OPTIONS.filter(
                      (t) => !state.start_time || t.value > state.start_time,
                    ).map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={
                saving ||
                (state.is_available && (!state.start_time || !state.end_time))
              }
              style={{
                width: "100%",
                backgroundColor: saved
                  ? colors.green
                  : saving
                    ? colors.slate
                    : colors.teal,
                color: "white",
                border: "none",
                padding: "11px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "700",
                cursor: "pointer",
              }}
            >
              {saved ? "✅ Saved!" : saving ? "Saving..." : "Save"}
            </button>
          </>
        )}
      </>
    );

    if (inline) {
      return (
        <div
          style={{
            backgroundColor: "#f8fffe",
            border: `1px solid ${colors.teal}`,
            borderRadius: "8px",
            padding: "16px",
            marginTop: "8px",
          }}
        >
          {panelContent}
        </div>
      );
    }

    // Mobile bottom sheet
    return (
      <>
        <div
          onClick={() => setSelectedDay(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 299,
          }}
        />
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: colors.white,
            borderRadius: "16px 16px 0 0",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
            padding: "20px",
            zIndex: 300,
          }}
        >
          <div
            style={{
              width: "40px",
              height: "4px",
              borderRadius: "2px",
              backgroundColor: colors.border,
              margin: "0 auto 16px",
            }}
          />
          {panelContent}
        </div>
      </>
    );
  };

  return (
    <div
      style={{
        padding: isMobile ? "12px" : "24px",
        maxWidth: "640px",
        height: "100vh",
        overflowY: "auto",
      }}
    >
      <h1
        style={{
          fontSize: "18px",
          fontWeight: "700",
          color: colors.darkGrey,
          marginBottom: "12px",
        }}
      >
        My Roster
      </h1>

      {/* View toggle */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
        {["week", "month"].map((mode) => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            style={{
              padding: "7px 16px",
              borderRadius: "20px",
              border: "none",
              backgroundColor:
                viewMode === mode ? colors.teal : colors.lightGrey,
              color: viewMode === mode ? "white" : colors.slate,
              fontWeight: "600",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            {mode === "week" ? "Week" : "Month"}
          </button>
        ))}
      </div>

      {/* Navigation */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: colors.white,
          borderRadius: "10px",
          padding: "10px 14px",
          marginBottom: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
        }}
      >
        <button
          onClick={() =>
            viewMode === "week"
              ? setWeekOffset((w) => Math.max(0, w - 1))
              : setMonthOffset((m) => Math.max(0, m - 1))
          }
          disabled={viewMode === "week" ? weekOffset === 0 : monthOffset === 0}
          style={{
            background: "none",
            border: "none",
            fontSize: "22px",
            cursor: "pointer",
            color: colors.teal,
            padding: "0 8px",
          }}
        >
          ‹
        </button>
        <div
          style={{
            fontWeight: "700",
            fontSize: "14px",
            color: colors.darkGrey,
          }}
        >
          {viewMode === "week" ? weekLabel : monthName}
        </div>
        <button
          onClick={() =>
            viewMode === "week"
              ? setWeekOffset((w) => Math.min(25, w + 1))
              : setMonthOffset((m) => Math.min(11, m + 1))
          }
          style={{
            background: "none",
            border: "none",
            fontSize: "22px",
            cursor: "pointer",
            color: colors.teal,
            padding: "0 8px",
          }}
        >
          ›
        </button>
      </div>

      {/* WEEK VIEW */}
      {viewMode === "week" && (
        <div>
          {weekDates.map((date) => {
            const dateStr = formatDate(date);
            const entry = rosterMap[dateStr];
            const past = isPast(date);
            const today = isToday(date);
            const isAvail = entry?.is_available;
            const isSelected = selectedDay === dateStr;

            return (
              <div key={dateStr} style={{ marginBottom: "8px" }}>
                <div
                  onClick={() => setSelectedDay(isSelected ? null : dateStr)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: colors.white,
                    borderRadius: isSelected ? "8px 8px 0 0" : "8px",
                    padding: "12px 14px",
                    cursor: "pointer",
                    border: `2px solid ${isSelected ? colors.teal : today ? colors.teal : isAvail ? "#b2dfdb" : colors.border}`,
                    borderBottom: isSelected
                      ? `2px solid ${colors.teal}`
                      : undefined,
                    opacity: past ? 0.6 : 1,
                  }}
                >
                  {/* Date circle */}
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: today
                        ? colors.teal
                        : isAvail
                          ? "#e0f7f4"
                          : colors.lightGrey,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "12px",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: "600",
                        color: today ? "white" : colors.slate,
                        lineHeight: 1,
                      }}
                    >
                      {date
                        .toLocaleDateString("en-AU", { weekday: "short" })
                        .toUpperCase()}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        color: today ? "white" : colors.darkGrey,
                        lineHeight: 1.2,
                      }}
                    >
                      {date.getDate()}
                    </div>
                  </div>

                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: colors.darkGrey,
                      }}
                    >
                      {date.toLocaleDateString("en-AU", { weekday: "long" })}
                    </div>
                    <div style={{ fontSize: "12px", color: colors.slate }}>
                      {entry
                        ? isAvail
                          ? `✅ ${timeLabel(entry.start_time)} – ${timeLabel(entry.end_time)}`
                          : "❌ Unavailable"
                        : "Not set — tap to update"}
                    </div>
                  </div>

                  <div style={{ color: colors.teal, fontSize: "18px" }}>
                    {isSelected ? "∧" : "›"}
                  </div>
                </div>

                {/* Desktop inline panel */}
                {isSelected && !isMobile && renderEditPanel(dateStr, true)}
              </div>
            );
          })}
        </div>
      )}

      {/* MONTH VIEW */}
      {viewMode === "month" && (
        <div
          style={{
            backgroundColor: colors.white,
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              backgroundColor: colors.darkGrey,
            }}
          >
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  padding: "8px 0",
                  fontSize: "11px",
                  fontWeight: "700",
                  color:
                    i >= 5 ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.7)",
                }}
              >
                {d}
              </div>
            ))}
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}
          >
            {grid.map((date, idx) => {
              if (!date)
                return (
                  <div
                    key={idx}
                    style={{
                      padding: "8px 4px",
                      minHeight: "52px",
                      backgroundColor: "#fafafa",
                      borderRight: `1px solid ${colors.border}`,
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  />
                );

              const dateStr = formatDate(date);
              const entry = rosterMap[dateStr];
              const past = isPast(date);
              const today = isToday(date);
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const isAvail = entry?.is_available;
              const isSelected = selectedDay === dateStr;

              let bgColor = colors.white;
              if (isWeekend) bgColor = "#fafafa";
              else if (isSelected) bgColor = "#e0f7f4";
              else if (isAvail) bgColor = "#e8f8f5";
              else if (entry && !isAvail) bgColor = "#fef5f5";

              return (
                <div
                  key={idx}
                  onClick={() =>
                    !isWeekend && setSelectedDay(isSelected ? null : dateStr)
                  }
                  style={{
                    padding: "6px 4px",
                    minHeight: "52px",
                    backgroundColor: bgColor,
                    borderRight: `1px solid ${colors.border}`,
                    borderBottom: `1px solid ${colors.border}`,
                    cursor: isWeekend ? "default" : "pointer",
                    opacity: past ? 0.5 : 1,
                    outline: isSelected ? `2px solid ${colors.teal}` : "none",
                  }}
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      borderRadius: "50%",
                      backgroundColor: today ? colors.teal : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 2px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: today ? "700" : "400",
                        color: today
                          ? "white"
                          : isWeekend
                            ? "#ccc"
                            : colors.darkGrey,
                      }}
                    >
                      {date.getDate()}
                    </span>
                  </div>

                  {!isWeekend && entry && (
                    <div
                      style={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: isAvail ? colors.teal : colors.red,
                        margin: "0 auto",
                      }}
                    />
                  )}

                  {!isWeekend && isAvail && entry.start_time && (
                    <div
                      style={{
                        fontSize: "9px",
                        color: colors.teal,
                        textAlign: "center",
                        fontWeight: "600",
                        marginTop: "2px",
                        lineHeight: 1,
                      }}
                    >
                      {timeLabel(entry.start_time)
                        .replace(" AM", "a")
                        .replace(" PM", "p")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Month view inline panel below grid */}
          {selectedDay && !isMobile && (
            <div
              style={{ padding: "16px", borderTop: `2px solid ${colors.teal}` }}
            >
              {renderEditPanel(selectedDay, true)}
            </div>
          )}

          <div
            style={{
              padding: "10px 14px",
              display: "flex",
              gap: "16px",
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            {[
              { color: colors.teal, label: "Available" },
              { color: colors.red, label: "Unavailable" },
              { color: colors.border, label: "Not set" },
            ].map((item) => (
              <div
                key={item.label}
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: item.color,
                  }}
                />
                <span style={{ fontSize: "11px", color: colors.slate }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mobile bottom sheet only */}
      {selectedDay && isMobile && renderEditPanel(selectedDay, false)}

      <div style={{ height: "40px" }} />
    </div>
  );
}
